import { useQuery, useMutation } from "@tanstack/react-query";
import type { User } from "@shared/schema";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { queryClient, apiRequest, getCsrfHeaders } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { X } from "lucide-react";

const MEMBERSHIP_TIERS = ["Free", "SearchAndBid", "Paid", "Annual", "Cancelled", "Suspended"] as const;
type MembershipTier = typeof MEMBERSHIP_TIERS[number];

function displayTierName(tier: string): string {
  if (tier === "Paid") return "Unlimited";
  return tier;
}

function formatDate(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return "Never";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "Never";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function UserDetailModal({ user, onClose }: { user: User; onClose: () => void }) {
  const { data: stats } = useQuery<{
    loadsPosted: number;
    partnersCount: number;
    bidsSubmitted: number;
  }>({
    queryKey: [`/api/admin/users/${user.id}/stats`],
    enabled: !!user.id,
  });

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="dialog-user-detail">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle className="text-lg">
                {user.firstName} {user.lastName}
              </DialogTitle>
              <DialogDescription className="mt-0.5">{user.email}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 pt-1">
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Account Details</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Joined</p>
                <p data-testid="text-detail-joined">{formatDateTime(user.createdAt)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Last Active</p>
                <p data-testid="text-detail-last-active">{formatDate(user.lastActiveAt)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Membership Tier</p>
                <p data-testid="text-detail-tier">{user.membershipTier}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Billing Cycle</p>
                <p data-testid="text-detail-billing-cycle">{user.billingCycle || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Email Verified</p>
                <p data-testid="text-detail-email-verified">{user.emailVerified ? "Yes" : "No"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Completed Shipments</p>
                <p data-testid="text-detail-completed-shipments">{user.completedShipments ?? 0}</p>
              </div>
              {user.company && (
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Company</p>
                  <p data-testid="text-detail-company">{user.company}</p>
                </div>
              )}
              {user.phone && (
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Phone</p>
                  <p data-testid="text-detail-phone">{user.phone}</p>
                </div>
              )}
              {user.dotNumber && (
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">DOT Number</p>
                  <p data-testid="text-detail-dot-number">{user.dotNumber}</p>
                </div>
              )}
              {user.mcNumber && (
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">MC Number</p>
                  <p data-testid="text-detail-mc-number">{user.mcNumber}</p>
                </div>
              )}
              {user.isSuspended && (
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground mb-0.5">Suspension Reason</p>
                  <p className="text-red-600" data-testid="text-detail-suspension-reason">{user.suspensionReason || "No reason provided"}</p>
                </div>
              )}
              <div className="col-span-2">
                <p className="text-xs text-muted-foreground mb-0.5">Stripe Customer ID</p>
                <p className="font-mono text-xs break-all" data-testid="text-detail-stripe-id">
                  {user.stripeCustomerId || "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Referral Code</p>
                <p className="font-mono text-xs" data-testid="text-detail-referral-code">{user.referralCode || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Billing Credits</p>
                <p data-testid="text-detail-credits">{user.billingCredits ?? 0}</p>
              </div>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Activity</p>
            <div className="grid grid-cols-3 gap-x-6 gap-y-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Loads Posted</p>
                <p className="text-lg font-semibold" data-testid="text-detail-loads-posted">{stats?.loadsPosted ?? "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Partners</p>
                <p className="text-lg font-semibold" data-testid="text-detail-partners-count">{stats?.partnersCount ?? "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Active/Won Bids</p>
                <p className="text-lg font-semibold" data-testid="text-detail-bids-submitted">{stats?.bidsSubmitted ?? "—"}</p>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminUsers() {
  const { toast } = useToast();
  const [filter, setFilter] = useState<string>("all");
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [suspendTargetUserId, setSuspendTargetUserId] = useState<string | null>(null);
  const [suspendReason, setSuspendReason] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetUserId, setDeleteTargetUserId] = useState<string | null>(null);
  const [deleteTargetEmail, setDeleteTargetEmail] = useState<string>("");
  const [impersonateDialogOpen, setImpersonateDialogOpen] = useState(false);
  const [impersonateTargetUserId, setImpersonateTargetUserId] = useState<string | null>(null);
  const [impersonateTargetName, setImpersonateTargetName] = useState<string>("");
  const [detailUser, setDetailUser] = useState<User | null>(null);
  const [tierDialogOpen, setTierDialogOpen] = useState(false);
  const [tierTargetUserId, setTierTargetUserId] = useState<string | null>(null);
  const [tierTargetEmail, setTierTargetEmail] = useState("");
  const [selectedTier, setSelectedTier] = useState<MembershipTier | null>(null);
  const [tierChangeReason, setTierChangeReason] = useState("");
  const [clearStripeSubscription, setClearStripeSubscription] = useState(false);

  const { data: users, isLoading, error } = useQuery<User[]>({
    queryKey: ["/api/admin/users"],
  });

  const updateMembership = useMutation({
    mutationFn: async ({
      userId,
      tier,
      cancellationReason,
      clearStripeSubscriptionId,
    }: {
      userId: string;
      tier: string;
      cancellationReason?: string;
      clearStripeSubscriptionId?: boolean;
    }) => {
      return apiRequest("PATCH", `/api/admin/users/${userId}`, {
        membershipTier: tier,
        ...(cancellationReason !== undefined ? { cancellationReason } : {}),
        ...(clearStripeSubscriptionId ? { stripeSubscriptionId: null } : {}),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/analytics"] });
      setTierDialogOpen(false);
      setTierTargetUserId(null);
      setTierTargetEmail("");
      setSelectedTier(null);
      setTierChangeReason("");
      setClearStripeSubscription(false);
      toast({
        title: "Success",
        description: "Membership tier updated successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update membership tier",
        variant: "destructive",
      });
    },
  });

  const openTierDialog = (user: User, tier: MembershipTier) => {
    setTierTargetUserId(user.id);
    setTierTargetEmail(user.email || user.id);
    setSelectedTier(tier);
    setTierChangeReason("");
    setClearStripeSubscription(false);
    setTierDialogOpen(true);
  };

  const closeTierDialog = () => {
    setTierDialogOpen(false);
    setTierTargetUserId(null);
    setTierTargetEmail("");
    setSelectedTier(null);
    setTierChangeReason("");
    setClearStripeSubscription(false);
  };

  const suspendUser = useMutation({
    mutationFn: async ({ userId, reason }: { userId: string; reason: string }) => {
      return apiRequest("POST", `/api/admin/users/${userId}/suspend`, { reason });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "User Suspended", description: "The user has been suspended." });
      setSuspendDialogOpen(false);
      setSuspendTargetUserId(null);
      setSuspendReason("");
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to suspend user",
        variant: "destructive",
      });
    },
  });

  const unsuspendUser = useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      return apiRequest("POST", `/api/admin/users/${userId}/unsuspend`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "User Unsuspended", description: "The user's account has been reactivated." });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to unsuspend user",
        variant: "destructive",
      });
    },
  });

  const deleteUser = useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      return apiRequest("DELETE", `/api/admin/users/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/analytics"] });
      setDeleteDialogOpen(false);
      setDeleteTargetUserId(null);
      setDeleteTargetEmail("");
      toast({ title: "User Deleted", description: "The user account and all related data have been permanently deleted." });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete user",
        variant: "destructive",
      });
    },
  });

  const handleImpersonate = (userId: string, firstName: string) => {
    setImpersonateTargetUserId(userId);
    setImpersonateTargetName(firstName);
    setImpersonateDialogOpen(true);
  };

  const confirmImpersonate = async () => {
    if (!impersonateTargetUserId) return;
    const res = await fetch(`/api/admin/impersonate/${impersonateTargetUserId}`, {
      method: "POST",
      credentials: "include",
      headers: getCsrfHeaders("POST"),
    });
    if (res.ok) {
      window.location.href = "/dashboard";
    } else {
      const data = await res.json();
      setImpersonateDialogOpen(false);
      toast({ title: "Cannot impersonate", description: data.error, variant: "destructive" });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="text-lg text-muted-foreground">Loading users...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="text-lg text-destructive mb-2">Access Denied</div>
          <div className="text-sm text-muted-foreground">You do not have permission to access the admin panel.</div>
        </div>
      </div>
    );
  }

  if (!users) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="text-lg text-muted-foreground">No users found</div>
        </div>
      </div>
    );
  }

  const filteredUsers = filter === "all"
    ? users
    : filter === "suspended"
    ? users.filter(u => u.isSuspended)
    : filter === "paid" || filter === "annual" || filter === "payment-suspended" || filter === "cancelled"
    ? users.filter(u => {
        if (filter === "payment-suspended") return u.membershipTier === "Suspended";
        if (filter === "cancelled") return u.membershipTier === "Cancelled";
        return u.membershipTier?.toLowerCase() === filter;
      })
    : users.filter(u => u.userType?.toLowerCase() === filter);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-wide" data-testid="text-admin-users-title">User Management</h1>
          <p className="text-muted-foreground">Manage platform users and memberships</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px]" data-testid="select-user-filter">
            <SelectValue placeholder="Filter users" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Users</SelectItem>
            <SelectItem value="shipper">Shippers</SelectItem>
            <SelectItem value="carrier">Carriers</SelectItem>
            <SelectItem value="broker">Brokers</SelectItem>
            <SelectItem value="paid">Unlimited — Shipper (Monthly)</SelectItem>
            <SelectItem value="annual">Paid (Annual)</SelectItem>
            <SelectItem value="payment-suspended">Payment Suspended</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="suspended">Admin Suspended</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Users ({filteredUsers.length})</CardTitle>
          <CardDescription>Complete list of registered users</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>User Type</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead>Membership</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Transactions</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id} data-testid={`row-user-${user.id}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        className="text-left underline-offset-4 hover:underline focus:outline-none"
                        onClick={() => setDetailUser(user)}
                        data-testid={`button-user-name-${user.id}`}
                      >
                        {user.firstName} {user.lastName}
                      </button>
                      {user.isTestAccount && (
                        <Badge variant="secondary" data-testid={`badge-demo-${user.id}`}>
                          Demo Account
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {user.userType ? (
                      <Badge variant="outline" data-testid={`badge-user-type-${user.id}`}>{user.userType}</Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">Not set</span>
                    )}
                  </TableCell>
                  <TableCell>{user.company || "-"}</TableCell>
                  <TableCell data-testid={`text-joined-${user.id}`}>
                    <span className="text-sm">{formatDate(user.createdAt)}</span>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        user.membershipTier === "Paid" || user.membershipTier === "Annual"
                          ? "default"
                          : user.membershipTier === "Suspended" || user.membershipTier === "Cancelled"
                          ? "destructive"
                          : "secondary"
                      }
                      data-testid={`badge-membership-${user.id}`}
                    >
                      {displayTierName(user.membershipTier ?? "")}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {user.isSuspended ? (
                      <Badge variant="destructive" data-testid={`badge-suspended-${user.id}`}>
                        Suspended
                      </Badge>
                    ) : (
                      <Badge variant="secondary" data-testid={`badge-active-${user.id}`}>
                        Active
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell data-testid={`text-transactions-${user.id}`}>
                    {user.transactionCount}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2 flex-wrap">
                      {user.membershipTier === "Free" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateMembership.mutate({ userId: user.id, tier: "Paid" })}
                          disabled={updateMembership.isPending}
                          data-testid={`button-upgrade-${user.id}`}
                        >
                          Upgrade to Unlimited
                        </Button>
                      )}
                      <Select
                        value=""
                        onValueChange={(tier) => openTierDialog(user, tier as MembershipTier)}
                      >
                        <SelectTrigger
                          className="w-[150px]"
                          data-testid={`select-membership-tier-${user.id}`}
                        >
                          <SelectValue placeholder="Set tier..." />
                        </SelectTrigger>
                        <SelectContent>
                          {MEMBERSHIP_TIERS.map((tier) => (
                            <SelectItem key={tier} value={tier}>
                              {tier}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {user.isSuspended ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => unsuspendUser.mutate({ userId: user.id })}
                          disabled={unsuspendUser.isPending}
                          data-testid={`button-unsuspend-${user.id}`}
                        >
                          Unsuspend
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            setSuspendTargetUserId(user.id);
                            setSuspendReason("");
                            setSuspendDialogOpen(true);
                          }}
                          disabled={suspendUser.isPending}
                          data-testid={`button-suspend-${user.id}`}
                        >
                          Suspend
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleImpersonate(user.id, user.firstName ?? "")}
                        data-testid={`button-impersonate-${user.id}`}
                      >
                        View as User
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive"
                        onClick={() => {
                          setDeleteTargetUserId(user.id);
                          setDeleteTargetEmail(user.email || user.id);
                          setDeleteDialogOpen(true);
                        }}
                        data-testid={`button-delete-${user.id}`}
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {detailUser && (
        <UserDetailModal user={detailUser} onClose={() => setDetailUser(null)} />
      )}

      <Dialog
        open={tierDialogOpen}
        onOpenChange={(open) => {
          if (!open) closeTierDialog();
        }}
      >
        <DialogContent data-testid="dialog-set-membership-tier">
          <DialogHeader>
            <DialogTitle>Set Membership Tier</DialogTitle>
            <DialogDescription>
              Confirm changing <span className="font-semibold">{tierTargetEmail}</span> to{" "}
              <span className="font-semibold">{selectedTier}</span>. This changes live account access.
            </DialogDescription>
          </DialogHeader>

          {(selectedTier === "Cancelled" || selectedTier === "Suspended") && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="tier-change-reason" className="text-sm font-medium">
                  Reason or note
                </label>
                <Textarea
                  id="tier-change-reason"
                  placeholder={`Brief reason for setting this account to ${selectedTier}`}
                  value={tierChangeReason}
                  onChange={(e) => setTierChangeReason(e.target.value)}
                  data-testid="input-tier-change-reason"
                />
              </div>

              <div className="flex items-start gap-3 rounded-md border p-3">
                <Checkbox
                  id="clear-stripe-subscription"
                  checked={clearStripeSubscription}
                  onCheckedChange={(checked) => setClearStripeSubscription(checked === true)}
                  data-testid="checkbox-clear-stripe-subscription"
                />
                <label htmlFor="clear-stripe-subscription" className="text-sm leading-5">
                  I confirm the underlying Stripe subscription is gone or inactive. Clear the stored
                  Stripe subscription ID.
                </label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeTierDialog}
              data-testid="button-cancel-tier-change"
            >
              Cancel
            </Button>
            <Button
              variant={selectedTier === "Cancelled" || selectedTier === "Suspended" ? "destructive" : "default"}
              onClick={() => {
                if (!tierTargetUserId || !selectedTier) return;
                updateMembership.mutate({
                  userId: tierTargetUserId,
                  tier: selectedTier,
                  ...((selectedTier === "Cancelled" || selectedTier === "Suspended")
                    ? {
                        cancellationReason: tierChangeReason.trim(),
                        clearStripeSubscriptionId: clearStripeSubscription,
                      }
                    : {}),
                });
              }}
              disabled={
                updateMembership.isPending ||
                !selectedTier ||
                ((selectedTier === "Cancelled" || selectedTier === "Suspended") && !tierChangeReason.trim())
              }
              data-testid="button-confirm-tier-change"
            >
              {updateMembership.isPending ? "Applying..." : "Confirm Tier Change"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={suspendDialogOpen} onOpenChange={setSuspendDialogOpen}>
        <DialogContent data-testid="dialog-suspend-reason">
          <DialogHeader>
            <DialogTitle>Suspend User</DialogTitle>
            <DialogDescription>
              Provide a reason for suspending this user. The user will be unable to log in or access the platform.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Reason for suspension (optional)"
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value)}
            data-testid="input-suspend-reason"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setSuspendDialogOpen(false);
                setSuspendTargetUserId(null);
                setSuspendReason("");
              }}
              data-testid="button-cancel-suspend"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (suspendTargetUserId) {
                  suspendUser.mutate({ userId: suspendTargetUserId, reason: suspendReason });
                }
              }}
              disabled={suspendUser.isPending}
              data-testid="button-confirm-suspend"
            >
              {suspendUser.isPending ? "Suspending..." : "Confirm Suspend"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent data-testid="dialog-delete-user">
          <DialogHeader>
            <DialogTitle>Delete User Account</DialogTitle>
            <DialogDescription>
              This will permanently delete the account for <span className="font-semibold">{deleteTargetEmail}</span> and all associated data (loads, trucks, messages, etc.). This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setDeleteTargetUserId(null);
                setDeleteTargetEmail("");
              }}
              data-testid="button-cancel-delete-user"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (deleteTargetUserId) {
                  deleteUser.mutate({ userId: deleteTargetUserId });
                }
              }}
              disabled={deleteUser.isPending}
              data-testid="button-confirm-delete-user"
            >
              {deleteUser.isPending ? "Deleting..." : "Delete Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={impersonateDialogOpen} onOpenChange={setImpersonateDialogOpen}>
        <DialogContent data-testid="dialog-impersonate-user">
          <DialogHeader>
            <DialogTitle>View as {impersonateTargetName}</DialogTitle>
            <DialogDescription>
              You are about to view Fill The Trucks as {impersonateTargetName}. An orange banner will appear at the top of every page — click it to return to the admin panel.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setImpersonateDialogOpen(false);
                setImpersonateTargetUserId(null);
                setImpersonateTargetName("");
              }}
              data-testid="button-cancel-impersonate"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmImpersonate}
              data-testid="button-confirm-impersonate"
            >
              View as {impersonateTargetName}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
